import { authentication } from 'firebaseconfig';

/** Refresh Firebase ID token this many ms before it expires (~1h lifetime). */
const REFRESH_BEFORE_MS = 5 * 60 * 1000;

let refreshTimeoutId = null;

export const stopTokenRefresh = () => {
  if (refreshTimeoutId !== null) {
    clearTimeout(refreshTimeoutId);
    refreshTimeoutId = null;
  }
};

const scheduleTokenRefresh = (expirationTime, onTokenRefreshed) => {
  stopTokenRefresh();

  const expiresAt = new Date(expirationTime).getTime();
  const refreshAt = expiresAt - REFRESH_BEFORE_MS;
  const delay = Math.max(refreshAt - Date.now(), 0);

  refreshTimeoutId = setTimeout(async () => {
    const user = authentication.currentUser;
    if (!user) return;

    try {
      const idTokenResult = await user.getIdTokenResult(true);
      if (onTokenRefreshed) {
        onTokenRefreshed(idTokenResult.token);
      } else {
        window.localStorage.setItem('token', idTokenResult.token);
      }
      scheduleTokenRefresh(idTokenResult.expirationTime, onTokenRefreshed);
    } catch (err) {
      console.error('Scheduled token refresh failed', err);
    }
  }, delay);
};

/** Start (or restart) proactive refresh timer for the current Firebase user. */
export const startTokenRefresh = async (onTokenRefreshed) => {
  const user = authentication.currentUser;
  if (!user) return;

  try {
    const idTokenResult = await user.getIdTokenResult(false);
    scheduleTokenRefresh(idTokenResult.expirationTime, onTokenRefreshed);
  } catch (err) {
    console.error('Failed to start token refresh schedule', err);
  }
};

/**
 * Returns a valid token for API calls. Refreshes immediately if expiry is
 * within REFRESH_BEFORE_MS; also ensures the background timer is running.
 */
export const ensureFreshToken = async (onTokenRefreshed) => {
  const user = authentication.currentUser;
  if (!user) {
    return window.localStorage.getItem('token');
  }

  try {
    const idTokenResult = await user.getIdTokenResult(false);
    const expiresAt = new Date(idTokenResult.expirationTime).getTime();
    const needsRefresh = expiresAt - Date.now() <= REFRESH_BEFORE_MS;

    if (needsRefresh) {
      const fresh = await user.getIdTokenResult(true);
      if (onTokenRefreshed) {
        onTokenRefreshed(fresh.token);
      } else {
        window.localStorage.setItem('token', fresh.token);
      }
      scheduleTokenRefresh(fresh.expirationTime, onTokenRefreshed);
      return fresh.token;
    }

    if (refreshTimeoutId === null) {
      scheduleTokenRefresh(idTokenResult.expirationTime, onTokenRefreshed);
    }

    return idTokenResult.token;
  } catch (err) {
    console.error('ensureFreshToken failed', err);
    return window.localStorage.getItem('token');
  }
};
